import { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, BoxProps, Button, FormLabel, useTheme } from '@mui/material';
import { useField } from 'formik';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { useCurrency } from 'src/hooks/useCurrency';

export type OptionTypes = {
    value: string;
    label: string;
};

interface CustomCounter {
    name: string;
    label?: string;
    boxStyling?: BoxProps;
    placeholder?: string;
    min?: number;
    max?: number;
    percentage?: boolean;
    currency?: boolean;
    disabled?: boolean;
}

export const CustomCounter: React.FC<CustomCounter> = ({
    label,
    boxStyling,
    placeholder,
    name,
    min,
    max,
    percentage,
    currency,
    disabled,
}) => {
    const { currencySymbol } = useCurrency();
    const [{ value }, { error, touched }, { setValue }] = useField(name);
    const { palette } = useTheme();
    const [count, setCount] = useState<number>(Number.parseFloat(value));

    // Update the field value in the parent component
    useEffect(() => {
        setValue(count);
    }, [count]);

    // Increment the count value by +1
    const increment = () => {
        setCount((prevCount) => {
            return !Number.isNaN(prevCount) && (prevCount as unknown as string) !== ''
                ? Number.parseFloat(prevCount.toString()) + 1.0
                : 1.0;
        });
    };

    // Decrement the count value by -1
    const decrement = () => {
        if (count === 0) {
            return;
        }
        if (max && count >= max) {
            return max;
        }

        setCount((prevCount) => {
            return !Number.isNaN(prevCount) && (prevCount as unknown as string) !== ''
                ? Number.parseFloat(prevCount.toString()) - 1.0
                : 0.0;
        });
    };

    const hasError = error && touched;

    const borderColor = hasError ? 'red' : palette.primary.main;
    return (
        <Box display="flex" flexDirection="column" {...boxStyling}>
            {label && (
                <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                    {label}
                </FormLabel>
            )}
            <Box
                display="flex"
                flexDirection="row"
                border={`0px solid ${borderColor}`}
                overflow="hidden"
                gap={2}
                alignItems="center"
            >
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor="#F3A256"
                    height="32px"
                    width="32px"
                    borderRadius="8px"
                    color="white"
                    fontSize="32px"
                >
                    <Button
                        sx={{
                            width: '32px',
                            height: '100%',
                            padding: 0,
                            color: 'white',
                            fontSize: '32px',
                        }}
                        onClick={decrement}
                        disabled={disabled}
                    >
                        <RemoveIcon />
                    </Button>
                </Box>
                <InputBaseField
                    value={count}
                    onChange={(e) => {
                        if (min && Number(e.target.value) <= min) {
                            return setCount(min);
                        }
                        if (max && Number(e.target.value) >= max) {
                            return setCount(max);
                        }
                        setCount(Number.parseFloat(e.target.value));
                    }}
                    percentage={percentage || placeholder === '%'}
                    currency={currency || placeholder === currencySymbol ? currencySymbol : null}
                    placeholder={placeholder || label}
                    type="text"
                    name={name}
                    style={{
                        paddingLeft: '0px',
                        paddingRight: '0px',
                    }}
                    inputProps={{
                        style: {
                            textAlign: 'center',
                            borderWidth: 0,
                            padding: '16px 14px',
                            width: '100%',
                            borderBottom: `1px solid ${palette.primary.main}`,
                            position: 'relative',
                        },
                        onFocus: (e) => {
                            e.target.style.borderBottom = `2px solid ${palette.primary.main}`;
                        },
                        onBlur: (e) => {
                            e.target.style.borderBottom = '1px solid rgba(0, 0, 0, 0.42)';
                        },
                    }}
                    sx={{
                        '&.MuiInputBase-root': {
                            border: 'none !important',
                            '&:hover': {
                                border: 'none !important',
                            },
                            '&.Mui-focused': {
                                border: 'none !important',
                            },
                        },
                        '& input[type=number]': {
                            '-moz-appearance': 'textfield',
                        },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: (props) => {
                                    return props.palette.primary.main;
                                },
                            },
                            '&:hover fieldset': {
                                borderColor: (props) => {
                                    return props.palette.primary.main;
                                },
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: (props) => {
                                    return props.palette.primary.main;
                                },
                            },
                        },
                    }}
                />
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor="#F3A256"
                    height="32px"
                    width="32px"
                    borderRadius="8px"
                    color="white"
                    fontSize="32px"
                >
                    <Button
                        sx={{
                            width: '32px',
                            height: '100%',
                            padding: 0,
                            color: 'white',
                            fontSize: '32px',
                        }}
                        onClick={increment}
                        disabled={disabled}
                    >
                        <AddIcon />
                    </Button>
                </Box>
            </Box>
            {hasError && <label className="error">{error}</label>}
        </Box>
    );
};
